import React, { useRef, ChangeEvent, memo } from "react";
import clsx from "clsx";
import { UploadIcon, CloseIcon, FileErrorIcon } from "components/icons";
import { ScrollNavigation } from "./ScrollNavigation";
import { type StoredFile, MAX_IMAGES } from "hooks/useProcessFiles";

type IUploadAttachProps = {
  selectedFiles: Record<string, StoredFile>;
  acceptString?: string;
  processFiles: (
    files: FileList,
    event?: ChangeEvent<HTMLInputElement>
  ) => void;
  onRemovedCallback?: (fileId: string) => void;
  removeFile: (fileId: string) => void;
};

export const UploadAttach: React.FC<IUploadAttachProps> = memo(
  ({
    selectedFiles,
    acceptString,
    processFiles,
    removeFile,
    onRemovedCallback,
  }) => {
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (files) {
        processFiles(files, event);
      }
    };

    // 移除所选文件
    const handleRemoveImage = (fileId: string) => {
      removeFile(fileId);
      onRemovedCallback?.(fileId);
    };

    // Calculate scroll distance dynamically (you can adjust this logic)
    const cardWidth = 104; // Width of each card
    const gap = 32; // Gap between cards (gap-8 = 2rem = 32px assuming 1rem = 16px)
    const scrollDistance = cardWidth + gap;

    return (
      <div
        className={clsx(
          "flex flex-row items-center rounded-t-[12px] bg-[rgba(226,232,240,0.75)] gap-[16px] mx-[16px]",
          {
            "p-[12px]": Object.values(selectedFiles).length < MAX_IMAGES,
            "py-[12px] px-2": Object.values(selectedFiles).length >= MAX_IMAGES,
          }
        )}
      >
        {Object.values(selectedFiles).length < MAX_IMAGES && (
          <div className="border border-dashed border-[#94A3B8] bg-[#F8FAFC] flex flex-col items-center justify-center w-[64px] h-[66px] gap-[10px] rounded-[8px]">
            <label className="flex flex-row items-center justify-center gap-2 cursor-pointer w-full h-full">
              <UploadIcon />
              <input
                type="file"
                accept={acceptString}
                multiple
                className="hidden"
                onChange={handleFileChange}
                disabled={Object.values(selectedFiles).length >= MAX_IMAGES}
              />
            </label>
          </div>
        )}

        {Object.values(selectedFiles).length === 0 ? (
          <div className="flex flex-col gap-[4px] flex-1">
            <span className="text-[#171717] text-[16px] font-semibold">
             Upload image to edit or copy style, objects
            </span>
            <span className="text-[#737373] text-[14px]">
           Supports JPG, PNG, WEBP (Up-to 10MB). Max 1 image.
            </span>
          </div>
        ) : (
          <ScrollNavigation
            scrollContainerRef={scrollContainerRef}
            scrollDistance={scrollDistance}
            navLength={Object.values(selectedFiles)?.length}
            className="gap-8 pl-3 pr-1 py-[10px]"
          >
            {Object.values(selectedFiles).map(
              ({ id, preview, file, error, extension, isGenerated }) => (
                <div className="w-fit h-fit relative flex-shrink-0" key={id}>
                  <div
                    onClick={() => handleRemoveImage(id)}
                    className="w-[26px] h-[26px] bg-white rounded-full absolute top-[-6px] left-[-13px] flex flex-row items-center justify-center cursor-pointer"
                  >
                    <CloseIcon />
                  </div>

                  {preview && !error ? (
                    <div className="w-[104px] max-h-[70px] h-full rounded-[10px] overflow-hidden bg-[#D9D9D9] flex flex-row items-center justify-center">
                      <img
                        src={preview}
                        alt={file.name}
                        className="rounded-lg max-h-[68px] object-cover flex-shrink-0 max-w-[140px]"
                      />
                    </div>
                  ) : (
                    <div className="w-[104px] h-[70px] rounded-[10px] border-[0.6px] border-solid border-[#F87171] bg-[#FEE2E2] flex-shrink-0 flex flex-col items-center justify-center gap-1">
                      <FileErrorIcon />
                      <span className="text-[10px] text-[#F87171] inline-block px-4 text-center leading-3">
                        {error}
                      </span>
                    </div>
                  )}

                  {!error && extension && (
                    <div className="absolute rounded-[16px] bottom-[-10px] left-1/2 -translate-x-1/2 bg-white py-1 px-2 text-[10px] text-[#525252] uppercase">
                      {isGenerated ? "generated" : extension}
                    </div>
                  )}
                </div>
              )
            )}
          </ScrollNavigation>
        )}
      </div>
    );
  }
);
